import React from 'react';

import { Button } from '@/components/design-system/Button';
import { LoadingScreen } from '@/components/loaders/LoadingScreen';
import { AuthResponseBanner } from '@/components/ui/AuthResponseBanner';
import ShiplexLogo from '@/components/ui/ShiplexLogo';
import { ROUTES } from '@/consts/routes';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { AcceptInvitationForm } from './components/AcceptInvitationForm';
import { useAcceptInvitation } from './hooks/useAcceptInvitation';

export const AcceptInvitationScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { step, error, loading, isLoaded, hasTicket, handleSubmit } = useAcceptInvitation();

  if (!isLoaded || step === 'loading') {
    return <LoadingScreen message="loading.invitation" />;
  }

  if (step === 'migrated') {
    return (
      <div className="flex h-screen w-full scale-85 flex-col items-center justify-center 2xl:scale-100">
        <ShiplexLogo color="duotoneDark" className="h-10 w-auto" />
        <h3 className="pt-10 text-black/65">{t('acceptInvitation.migratedTitle')}</h3>
        <p className="text-typography-900/45 my-2 mb-6 w-81 text-center">{t('acceptInvitation.migratedMessage')}</p>
        <Button onClick={() => void navigate(ROUTES.user.passwordReset)}>{t('acceptInvitation.migratedButton')}</Button>
      </div>
    );
  }

  if (!hasTicket || step === 'error') {
    return (
      <div className="flex h-screen w-full scale-85 flex-col items-center justify-center 2xl:scale-100">
        <ShiplexLogo color="duotoneDark" className="h-10 w-auto" />
        <h3 className="pt-10 text-black/65">{t('acceptInvitation.invalidTitle')}</h3>
        <p className="text-typography-900/45 my-2 mb-6 w-81 text-center">{t('acceptInvitation.invalidMessage')}</p>
        <AuthResponseBanner error={error} className="mb-4" />
        <Link to={ROUTES.auth.login} className="text-primary hover:text-primary/75 transition-all duration-300">
          {t('acceptInvitation.backToLogin')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full scale-85 flex-col items-center justify-center 2xl:scale-100">
      <ShiplexLogo color="duotoneDark" className="h-10 w-auto" />
      <h3 className="pt-10 text-black/65">{t('acceptInvitation.title')}</h3>
      <p className="text-typography-900/45 my-2 mb-6 w-81 text-center">{t('acceptInvitation.subtitle')}</p>

      <AcceptInvitationForm onSubmit={handleSubmit} loading={loading} />

      <AuthResponseBanner error={error} className="mt-4" />
    </div>
  );
};
